import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/6804260/pexels-photo-6804260.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  { text: "FORGING PHYSICAL", accent: false },
  { text: "METALS & FINANCIAL", accent: true },
  { text: "PRECISION, GLOBALLY", accent: false },
];

const STATS = [
  { value: "25+", label: "Years Trading Expertise" },
  { value: "ISRI", label: "Scrap Grade Standard" },
  { value: "LME·COMEX·SME", label: "Exchanges Covered" },
  { value: "7+", label: "Global Port Hubs" },
];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Molten metal pouring in a foundry" className="w-full h-[120%] object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D12]/70 via-[#0A0D12]/55 to-[#0A0D12]" />
      </motion.div>
      <div className="absolute inset-0 -z-10 technical-grid opacity-60" />
      <div
        className="absolute -z-10 left-1/2 top-1/3 -translate-x-1/2 w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full heat-pulse"
        style={{ background: "radial-gradient(circle, rgba(224,109,40,0.16) 0%, transparent 60%)" }}
      />

      <motion.div style={{ opacity: fade }} className="flex-1 flex flex-col justify-center max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-16 pt-36 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8"
        >
          <span
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#F07D38] border border-[#E06D28]/40 bg-[#E06D28]/10 px-4 py-2 rounded-full"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#F3B700] animate-pulse" />
            25+ Years of Metal Trading Expertise
          </span>
        </motion.div>

        <h1 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[5.25rem]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "copper-text-gradient" : "text-[#F4F6F8]"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-metals-line"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#F3B700] mb-6"
        >
          Aluminium · Copper · Lead · Zinc
        </motion.p>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-8 text-base sm:text-lg text-[#94A3B8] max-w-2xl leading-relaxed"
        >
          ACPZ Consultancy Ltd specializes in high-value physical base metal procurement, scrap grading,
          international container logistics, and cross-exchange paper hedging across LME, COMEX, and SME.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-contact"
            onClick={(e) => { e.preventDefault(); onNavigate("#contact"); }}
            className="group inline-flex items-center gap-2 bg-[#E06D28] hover:bg-[#F07D38] text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(224,109,40,0.45)]"
          >
            Engage Consultancy Services
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#about"
            data-testid="hero-cta-explore"
            onClick={(e) => { e.preventDefault(); onNavigate("#about"); }}
            className="inline-flex items-center gap-2 border border-[#283244] hover:border-[#E06D28]/60 text-[#F4F6F8] font-medium px-8 py-4 rounded-full transition-colors duration-300"
          >
            Explore Capabilities
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="relative border-t border-[#283244]/70 backdrop-blur-sm bg-[#0A0D12]/40"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-2 lg:grid-cols-4" data-testid="hero-stats">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className={`py-7 pr-6 ${i > 0 ? "lg:border-l lg:border-[#283244]/70 lg:pl-8" : ""} ${i % 2 === 1 ? "border-l border-[#283244]/70 pl-6 lg:pl-8" : ""}`}
            >
              <div className="font-display font-extrabold text-xl sm:text-2xl text-[#F07D38]">{s.value}</div>
              <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#64748B] mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="absolute right-6 -top-16 hidden lg:flex flex-col items-center gap-2 text-[#64748B]">
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-[-8px]">Scroll</span>
          <ArrowDown size={16} className="animate-bounce mt-6" />
        </div>
      </motion.div>
    </section>
  );
}
